/**
 * Role hierarchy and page access configuration
 *
 * VIEWER:         Dashboard
 * AGENT:          Dashboard (own profile only)
 * SUPERVISOR:     Dashboard, Agents, Appels
 * ANALYST:        SUPERVISOR + Widgets, Mes Widgets, Marketplace
 * SENIOR_ANALYST: ANALYST + Fine-Tuning
 * DATASCIENCE:    SENIOR_ANALYST + Pipeline
 * ADMIN:          Toutes les pages
 */

export const ROLES = ['ADMIN', 'DATASCIENCE', 'SENIOR_ANALYST', 'ANALYST', 'SUPERVISOR', 'AGENT', 'VIEWER'] as const;
export type AppRole = (typeof ROLES)[number];

export const ROLE_LABELS: Record<AppRole, string> = {
  ADMIN: 'Admin',
  DATASCIENCE: 'Data Science',
  SENIOR_ANALYST: 'Analyste Senior',
  ANALYST: 'Analyste',
  SUPERVISOR: 'Superviseur',
  AGENT: 'Agent',
  VIEWER: 'Lecteur',
};

/**
 * Minimum role required for each route prefix.
 * Routes not listed here are accessible to all authenticated users.
 */
const ROUTE_ACCESS: Record<string, AppRole> = {
  '/agents': 'SUPERVISOR',
  '/nomenclature': 'SUPERVISOR',
  '/callback-promises': 'SUPERVISOR',
  '/manager-escalations': 'SUPERVISOR',
  '/calls': 'SUPERVISOR',
  '/widgets': 'ANALYST',
  '/mes-widgets': 'ANALYST',
  '/marketplace': 'ANALYST',
  '/knowledge-base': 'ANALYST',
  '/ai-kb': 'ADMIN',
  '/fine-tuning': 'SENIOR_ANALYST',
  '/training-segments': 'DATASCIENCE',
  '/pipeline': 'DATASCIENCE',
  '/satisfaction-survey': 'SUPERVISOR',
  '/concentrix-nps': 'SUPERVISOR',
  '/alerts': 'ADMIN',
  '/config': 'ADMIN',
  '/admin': 'ADMIN',
};

/**
 * Role hierarchy level (higher = more access)
 */
const ROLE_LEVEL: Record<AppRole, number> = {
  VIEWER: 0,
  AGENT: 1,
  SUPERVISOR: 2,
  ANALYST: 3,
  SENIOR_ANALYST: 4,
  DATASCIENCE: 5,
  ADMIN: 6,
};

export function hasAccess(userRole: string | null | undefined, requiredRole: AppRole): boolean {
  const level = ROLE_LEVEL[userRole as AppRole] ?? 0;
  const required = ROLE_LEVEL[requiredRole] ?? 0;
  return level >= required;
}

export function canAccessRoute(userRole: string | null | undefined, pathname: string): boolean {
  for (const [prefix, requiredRole] of Object.entries(ROUTE_ACCESS)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return hasAccess(userRole, requiredRole);
    }
  }
  // Dashboard and unlisted routes: all authenticated users
  return true;
}

export function getAccessibleNavItems(userRole: string | null | undefined) {
  return Object.entries(ROUTE_ACCESS).reduce(
    (acc, [prefix, required]) => {
      acc[prefix] = hasAccess(userRole, required);
      return acc;
    },
    {} as Record<string, boolean>,
  );
}
